import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField } from "@mui/material";
import React, { useState } from "react";
import SectionTitle from "../../components/common/SectionTitle";

type FlightClient = {
    Id?: number | string | null;
    id?: number | string | null;
    ClientCode?: string | null;
    client_code?: string | null;
    clientCode?: string | null;
    Name?: string | null;
    name?: string | null;
    PointOfSaleId?: number | string | null;
    point_of_sale_id?: number | string | null;
    pointOfSaleId?: number | string | null;
};

type SortConfig = {
    key: 'Id' | 'ClientCode' | 'PointOfSaleId' | 'Name';
    direction: 'asc' | 'desc';
};

type SearchState = {
    Id: string;
    ClientCode: string;
    Name: string;
    PointOfSaleId: string;
};

type SortableHeaderProps = {
    label: string;
    sortKey: SortConfig['key'];
    sortConfig: SortConfig;
    onSort: (value: SortConfig) => void;
    searchValue: string;
    onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

type FlightClientsProps = {
    clients: FlightClient[];
    selectedClient: FlightClient | null;
    setSelectedClient: (client: FlightClient | null) => void;
    loading: boolean;
};

const pointOfSaleMapping: Record<string, string> = {
    1113: 'Lightspeed',
    1139: 'RunIT',
    1140: 'Heartland',
    1200: 'Shopify Pilot',
    1201: 'LightspeedX',
    1210: 'LightSpeed R Sku v2',
    1202: 'Heartland Retail API',
    1203: 'RunIt RealTime API',
}

const SortableTableHeaderCell = ({ label, sortKey, sortConfig, onSort, searchValue, onSearch }: SortableHeaderProps) => {
    const handleSort = () => {
        let direction: SortConfig['direction'] = 'asc';
        if (sortConfig.key === sortKey && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        onSort({ key: sortKey, direction });
    };

    return (
        <TableCell>
            <TableSortLabel
                active={sortConfig.key === sortKey}
                direction={sortConfig.direction}
                onClick={handleSort}
            >
                {label}
            </TableSortLabel>
            <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchValue}
                onChange={onSearch}
            />
        </TableCell>
    );
}

const FlightClients = ({ clients, selectedClient, setSelectedClient, loading }: FlightClientsProps) => {
    const [search, setSearch] = useState<SearchState>({ Id: "", ClientCode: "", Name: "", PointOfSaleId: "" });
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'Id', direction: 'asc' });

    const normalizedClients = (clients ?? []).map((client) => ({
        ...client,
        Id: client?.Id ?? client?.id ?? null,
        ClientCode: client?.ClientCode ?? client?.client_code ?? client?.clientCode ?? '',
        Name: client?.Name ?? client?.name ?? '',
        PointOfSaleId: client?.PointOfSaleId ?? client?.point_of_sale_id ?? client?.pointOfSaleId ?? null,
    }));

    const sortedClients = [...normalizedClients].sort((a, b) => {
        const aValue = (a[sortConfig.key] ?? '').toString().toLowerCase();
        const bValue = (b[sortConfig.key] ?? '').toString().toLowerCase();

        if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });

        const searchKeys: Array<keyof SearchState> = ['Id', 'ClientCode', 'Name', 'PointOfSaleId'];

        const filteredClients = sortedClients.filter(client =>
                searchKeys.every((key) => {
                    if (!search[key]) {
            return true;
          }
          if (key === "PointOfSaleId") {
                        const posValue = client.PointOfSaleId;
                        const posName = pointOfSaleMapping[String(posValue ?? '')];
            if (!posName) {
              return false;
            }
            return posName.toLowerCase().includes(search[key].toLowerCase());
          }
                    const value = client[key];
          if (value == null) {
            return false;
          }
          return value.toString().toLowerCase().includes(search[key].toLowerCase());
        })
    );

    const selectClient = (client: FlightClient) => {
        console.log(loading)
        if (loading) {
            return;
        }
        setSelectedClient(client);
    }

    return (
        <div>
            <Paper elevation={2} sx={{mb:2}}>
                <Box sx={{mb:2}}>
                    <SectionTitle label="Clients" type="primary"/>
                    <Button variant="contained" color="primary" sx={{ m: 1 }}onClick={() => setSelectedClient(null)}>Unselect Client</Button>
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <SortableTableHeaderCell
                                        label="Id"
                                        sortKey="Id"
                                        sortConfig={sortConfig}
                                        onSort={setSortConfig}
                                        searchValue={search.Id}
                                        onSearch={e => setSearch({ ...search, Id: e.target.value })}
                                    />
                                    <SortableTableHeaderCell
                                        label="Code"
                                        sortKey="ClientCode"
                                        sortConfig={sortConfig}
                                        onSort={setSortConfig}
                                        searchValue={search.ClientCode}
                                        onSearch={e => setSearch({ ...search, ClientCode: e.target.value })}
                                    />
                                    <SortableTableHeaderCell
                                        label="POS"
                                        sortKey="PointOfSaleId"
                                        sortConfig={sortConfig}
                                        onSort={setSortConfig}
                                        searchValue={search.PointOfSaleId}
                                        onSearch={e => setSearch({ ...search, PointOfSaleId: e.target.value })}
                                    />
                                    <SortableTableHeaderCell
                                        label="Name"
                                        sortKey="Name"
                                        sortConfig={sortConfig}
                                        onSort={setSortConfig}
                                        searchValue={search.Name}
                                        onSearch={e => setSearch({ ...search, Name: e.target.value })}
                                    />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredClients.map((client) => (
                                    <TableRow 
                                        key={client.Id} 
                                        onClick={() => selectClient(client)}
                                        sx={{ cursor: 'pointer', backgroundColor: selectedClient && (selectedClient.Id ?? selectedClient.id) === client.Id ? '#f0f0f0' : 'inherit' }}
                                    >
                                        <TableCell>{client.Id}</TableCell>
                                        <TableCell>{client.ClientCode}</TableCell>
                                        <TableCell>{pointOfSaleMapping[String(client.PointOfSaleId ?? '')] || client.PointOfSaleId || ''}</TableCell>
                                        <TableCell>{client.Name}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>
        </div>
    );
}

export default FlightClients;
